import { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  Cpu,
  Smartphone,
  ShieldAlert,
  RefreshCw,
  Share2,
  CheckCircle2,
  Activity,
  CalendarDays,
  Tag,
  Layers,
  Microscope,
  Wrench,
  ListOrdered,
  ClipboardCheck,
  FileText,
  ChevronDown,
  Download,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { exportDiagnosisToPdf } from "../lib/pdfExport";

const SEVERITY_STYLES = {
  low: {
    bar: "bg-emerald-500",
    badge: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    glow: "shadow-emerald-500/20",
  },
  medium: {
    bar: "bg-amber-400",
    badge: "bg-amber-400/15 text-amber-300 border-amber-400/30",
    glow: "shadow-amber-400/20",
  },
  high: {
    bar: "bg-orange-500",
    badge: "bg-orange-500/15 text-orange-300 border-orange-500/30",
    glow: "shadow-orange-500/20",
  },
  critical: {
    bar: "bg-red-500",
    badge: "bg-red-500/15 text-red-300 border-red-500/30",
    glow: "shadow-red-500/30",
  },
};

function formatDate(iso, lang) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(lang || undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

function SectionHeader({ icon, children, testId }) {
  return (
    <h3
      className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-4 flex items-center gap-2"
      data-testid={testId}
    >
      {icon}
      {children}
    </h3>
  );
}

function Paragraphs({ text, testId }) {
  if (!text) return null;
  const parts = String(text).split(/\n\n+/);
  return (
    <div
      className="space-y-4 text-base leading-relaxed text-foreground/90"
      data-testid={testId}
    >
      {parts.map((p, i) => (
        <p key={i}>{p.trim()}</p>
      ))}
    </div>
  );
}

function RepairStep({ step, index, openByDefault }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(openByDefault);
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.03 }}
      className="rounded-2xl border border-border/80 bg-secondary/40 overflow-hidden"
      data-testid={`repair-step-${step.order}`}
    >
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-4 text-left px-5 py-4 hover:bg-secondary/80 transition"
        data-testid={`repair-step-toggle-${step.order}`}
      >
        <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary font-bold font-mono text-sm">
          {String(step.order).padStart(2, "0")}
        </span>
        <span className="flex-1 min-w-0">
          <span className="block font-semibold leading-snug">
            {step.title}
          </span>
          {step.objective && !open && (
            <span className="block text-xs text-muted-foreground mt-0.5 truncate">
              {step.objective}
            </span>
          )}
        </span>
        <ChevronDown
          className={`w-4 h-4 text-muted-foreground transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5 pt-1 space-y-4 text-sm leading-relaxed">
              {step.objective && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1">
                    {t("result.stepObjective")}
                  </div>
                  <p className="text-foreground/90">{step.objective}</p>
                </div>
              )}
              {(step.common_faults || []).length > 0 && (
                <StepList
                  label={t("result.stepCommonFaults")}
                  items={step.common_faults}
                  testId={`repair-step-${step.order}-faults`}
                />
              )}
              {(step.procedures || []).length > 0 && (
                <StepList
                  label={t("result.stepProcedures")}
                  items={step.procedures}
                  testId={`repair-step-${step.order}-procedures`}
                />
              )}
              {(step.expected_results || []).length > 0 && (
                <StepList
                  label={t("result.stepExpected")}
                  items={step.expected_results}
                  testId={`repair-step-${step.order}-expected`}
                />
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function StepList({ label, items, testId }) {
  return (
    <div data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1.5">
        {label}
      </div>
      <ul className="space-y-1.5">
        {items.map((it, i) => (
          <li key={i} className="flex items-start gap-2.5">
            <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function ResultCard({ data, onReset, hideReset = false }) {
  const { t, i18n } = useTranslation();
  const [shared, setShared] = useState(false);
  const [exporting, setExporting] = useState(false);
  if (!data) return null;
  const { parsed = {}, diagnosis = {}, created_at } = data;

  const sev = SEVERITY_STYLES[diagnosis.severity] || SEVERITY_STYLES.medium;
  const sevKey = diagnosis.severity
    ? diagnosis.severity.charAt(0).toUpperCase() + diagnosis.severity.slice(1)
    : "Medium";
  const sevLabel = t(`result.severity${sevKey}`);
  const confidence = Math.max(0, Math.min(100, Number(diagnosis.confidence) || 0));

  const primary =
    diagnosis.primary_faulty_component ||
    diagnosis.faulty_component ||
    "—";
  const involved = diagnosis.involved_components || [];
  const panicTag = diagnosis.panic_tag || diagnosis.panic_type || "";
  const steps = diagnosis.repair_steps || [];
  const replacementOrder = diagnosis.replacement_order || [];
  const postRepair = diagnosis.post_repair_notes || [];
  const technical =
    diagnosis.technical_analysis || diagnosis.explanation || "";

  const handleShare = async () => {
    const text = `${t("result.title")} — ${diagnosis.device_model || ""}\n${t(
      "result.primary",
    )}: ${primary}\n${t("result.panicType")}: ${diagnosis.panic_type || ""}\n${t(
      "result.confidence",
    )}: ${confidence}%`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "iDiag", text });
      } else {
        await navigator.clipboard.writeText(text);
        toast.success(t("result.copied"));
      }
      setShared(true);
      setTimeout(() => setShared(false), 1800);
    } catch {
      /* noop */
    }
  };

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      exportDiagnosisToPdf({ data, t, i18n });
    } catch (e) {
      toast.error(e?.message || "PDF error");
    } finally {
      setTimeout(() => setExporting(false), 400);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: [0.2, 0.7, 0.3, 1] }}
      className="rounded-3xl bg-card/90 border border-border/80 overflow-hidden gradient-border"
      data-testid="result-card"
    >
      {/* Hero: primary faulty component */}
      <div className="relative px-7 sm:px-10 py-8 border-b border-border/70">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-4">
            <div
              className={`h-14 w-14 rounded-2xl bg-secondary flex items-center justify-center ring-1 ring-white/5 shadow-xl ${sev.glow}`}
            >
              <Cpu className="w-7 h-7 text-primary" />
            </div>
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-1.5">
                {t("result.primary")}
              </div>
              <h2
                className="text-2xl sm:text-3xl font-extrabold leading-tight tracking-tight"
                data-testid="result-primary-component"
              >
                {primary}
              </h2>
              {panicTag && (
                <div
                  className="mt-2 inline-flex items-center gap-2 text-sm text-muted-foreground font-mono"
                  data-testid="result-panic-tag"
                >
                  <Tag className="w-3.5 h-3.5" />
                  <span className="truncate max-w-[32ch] sm:max-w-none">
                    {panicTag}
                  </span>
                </div>
              )}
              {diagnosis.panic_type && diagnosis.panic_type !== panicTag && (
                <div
                  className="mt-1.5 inline-flex items-center gap-1.5 text-xs text-muted-foreground/80"
                  data-testid="result-panic-type"
                >
                  <Activity className="w-3 h-3" />
                  {diagnosis.panic_type}
                </div>
              )}
            </div>
          </div>
          <span
            className={`inline-flex items-center gap-1.5 h-7 px-3 rounded-full text-xs font-semibold border ${sev.badge}`}
            data-testid="result-severity-badge"
          >
            <ShieldAlert className="w-3.5 h-3.5" />
            {t("result.severity")}: {sevLabel}
          </span>
        </div>

        {/* Confidence */}
        <div className="mt-6">
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
            <span>{t("result.confidence")}</span>
            <span
              className="font-mono text-foreground"
              data-testid="result-confidence-value"
            >
              {confidence}%
            </span>
          </div>
          <div className="h-2 w-full rounded-full bg-secondary overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${confidence}%` }}
              transition={{ duration: 0.9, ease: "easeOut" }}
              className={`h-full ${sev.bar}`}
              data-testid="result-confidence-bar"
            />
          </div>
        </div>
      </div>

      {/* Device meta */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-border/60">
        <Meta
          icon={<Smartphone className="w-4 h-4" />}
          label={t("result.device")}
          value={diagnosis.device_model || parsed.product || "—"}
          testId="result-meta-device"
        />
        <Meta
          icon={<Cpu className="w-4 h-4" />}
          label={t("result.cpu")}
          value={diagnosis.cpu || "—"}
          testId="result-meta-cpu"
        />
        <Meta
          icon={<Activity className="w-4 h-4" />}
          label={t("result.ios")}
          value={diagnosis.ios_version || parsed.os_version || "—"}
          testId="result-meta-ios"
        />
        <Meta
          icon={<CalendarDays className="w-4 h-4" />}
          label={t("result.savedAt")}
          value={formatDate(created_at || parsed.timestamp, i18n.resolvedLanguage)}
          testId="result-meta-date"
        />
      </div>

      <div className="p-7 sm:p-10 space-y-10">
        {/* Short description */}
        {diagnosis.short_description && (
          <section data-testid="result-short-description-section">
            <SectionHeader icon={<FileText className="w-3.5 h-3.5" />}>
              {t("result.shortDescription")}
            </SectionHeader>
            <p className="text-base leading-relaxed text-foreground/90" data-testid="result-short-description">
              {diagnosis.short_description}
            </p>
          </section>
        )}

        {/* Involved components */}
        {involved.length > 0 && (
          <section data-testid="result-involved-section">
            <SectionHeader icon={<Layers className="w-3.5 h-3.5" />}>
              {t("result.involved")}
            </SectionHeader>
            <div className="flex flex-wrap gap-2" data-testid="result-involved-list">
              {involved.map((c, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-2 h-8 px-3 rounded-full text-xs font-semibold bg-secondary border border-border/70 text-foreground/90"
                  data-testid={`result-involved-chip-${i}`}
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                  {c}
                </span>
              ))}
            </div>
          </section>
        )}

        {/* Technical analysis */}
        {technical && (
          <section data-testid="result-technical-section">
            <SectionHeader icon={<Microscope className="w-3.5 h-3.5" />}>
              {t("result.technicalAnalysis")}
            </SectionHeader>
            <Paragraphs text={technical} testId="result-technical-analysis" />
          </section>
        )}

        {/* Repair steps */}
        {steps.length > 0 && (
          <section data-testid="result-steps-section">
            <SectionHeader icon={<Wrench className="w-3.5 h-3.5" />}>
              {t("result.repairSteps")}
            </SectionHeader>
            <div className="space-y-2.5">
              {steps.map((s, i) => (
                <RepairStep key={s.order || i} step={s} index={i} openByDefault={i === 0} />
              ))}
            </div>
          </section>
        )}

        {/* Replacement order */}
        {replacementOrder.length > 0 && (
          <section data-testid="result-replacement-section">
            <SectionHeader icon={<ListOrdered className="w-3.5 h-3.5" />}>
              {t("result.replacementOrder")}
            </SectionHeader>
            <ol className="space-y-2" data-testid="result-replacement-list">
              {replacementOrder.map((r, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 p-3 rounded-xl bg-secondary/50 border border-border/60"
                >
                  <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary/20 text-primary font-bold font-mono text-xs">
                    {i + 1}
                  </span>
                  <span className="text-sm leading-relaxed pt-0.5">{r}</span>
                </li>
              ))}
            </ol>
          </section>
        )}

        {/* Post-repair notes */}
        {postRepair.length > 0 && (
          <section data-testid="result-postrepair-section">
            <SectionHeader icon={<ClipboardCheck className="w-3.5 h-3.5" />}>
              {t("result.postRepair")}
            </SectionHeader>
            <ul className="space-y-2" data-testid="result-postrepair-list">
              {postRepair.map((r, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm leading-relaxed">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Conclusion */}
        {diagnosis.conclusion && (
          <section data-testid="result-conclusion-section" className="rounded-2xl bg-primary/5 border border-primary/20 p-5 sm:p-6">
            <SectionHeader icon={<FileText className="w-3.5 h-3.5" />}>
              {t("result.conclusion")}
            </SectionHeader>
            <Paragraphs text={diagnosis.conclusion} testId="result-conclusion" />
          </section>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-3 px-7 sm:px-10 pb-8">
        {!hideReset && onReset && (
          <button
            onClick={onReset}
            data-testid="result-new-diagnostic-button"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-primary text-primary-foreground font-semibold hover:brightness-110 transition shadow-lg shadow-primary/20"
          >
            <RefreshCw className="w-4 h-4" />
            {t("result.new")}
          </button>
        )}
        <button
          onClick={handleExportPdf}
          disabled={exporting}
          data-testid="result-export-pdf-button"
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-secondary text-foreground font-semibold hover:bg-accent transition disabled:opacity-60"
        >
          {exporting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              {t("result.exporting")}
            </>
          ) : (
            <>
              <Download className="w-4 h-4" />
              {t("result.exportPdf")}
            </>
          )}
        </button>
        <button
          onClick={handleShare}
          data-testid="result-share-button"
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-secondary text-foreground font-semibold hover:bg-accent transition"
        >
          {shared ? (
            <>
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              {t("result.copied")}
            </>
          ) : (
            <>
              <Share2 className="w-4 h-4" />
              {t("result.share")}
            </>
          )}
        </button>
      </div>
    </motion.div>
  );
}

function Meta({ icon, label, value, testId }) {
  return (
    <div className="bg-card px-6 py-4" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground flex items-center gap-1.5 mb-1.5">
        {icon}
        <span>{label}</span>
      </div>
      <div className="text-sm font-semibold truncate">{value}</div>
    </div>
  );
}
